// src/hooks/useApi.js
import { useState, useCallback } from "react";
import axios from "axios";
import { useToast } from "./use-toast";

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: "https://api.geeth365.com/",
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token if available
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");

    // Debug: Check what token is being used
    // console.log("🔐 Token from localStorage:", token ? "Present" : "Missing");
    // console.log("🔐 Token value:", token);

    if (token) {
      // Force Bearer with capital B
      config.headers.Authorization = `Bearer ${token}`;
      // console.log("✅ Authorization header set:", config.headers.Authorization);
    } else {
      console.warn("⚠️ No token found in localStorage");
    }

    // console.log("🚀 API Request:", {
    //   url: config.url,
    //   method: config.method,
    //   headers: config.headers,
    // });

    return config;
  },
  (error) => {
    console.error("❌ Request interceptor error:", error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    // console.log("✅ API Response Success:", {
    //   url: response.config.url,
    //   status: response.status,
    // });
    return response;
  },
  (error) => {
    console.error("❌ API Error:", {
      url: error.config?.url,
      status: error.response?.status,
      message: error.message,
      response: error.response?.data,
    });

    // Handle 401 Unauthorized specifically
    if (error.response?.status === 401) {
      console.error("🔐 401 Unauthorized - Token might be invalid or expired");

      // Optional: Clear invalid tokens
      // localStorage.removeItem("access_token");
      // localStorage.removeItem("token_type");
      // window.location.href = '/auth';
    }

    return Promise.reject(error);
  }
);

// Rest of the useApi hook remains the same...
const useApi = (options = {}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const {
    method: defaultMethod = "POST",
    headers: defaultHeaders = {},
    showToast = true,
  } = options;

  const callApi = useCallback(
    async (url, requestOptions = {}) => {
      const {
        body,
        method = defaultMethod,
        headers = {},
        showToast: requestShowToast = showToast,
        ...otherOptions
      } = requestOptions;

      setLoading(true);
      setError(null);
      setData(null);

      try {
        const config = {
          url,
          method: method.toLowerCase(),
          headers: { ...defaultHeaders, ...headers },
          ...otherOptions,
        };

        // Add data based on method
        if (["post", "put", "patch"].includes(method.toLowerCase())) {
          config.data = body;
        } else if (["get", "delete"].includes(method.toLowerCase())) {
          config.params = body;
        }

        const response = await apiClient(config);
        setData(response.data);

        if (requestShowToast && response.data?.message) {
          toast({
            title: "Success",
            description: response.data.message,
            variant: "default",
          });
        }

        return { data: response.data, status: response.status };
      } catch (err) {
        const errorMessage =
          err.response?.data?.detail ||
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          "Something went wrong";

        setError(errorMessage);

        if (requestShowToast) {
          toast({
            title: "Error",
            description: errorMessage,
            variant: "destructive",
          });
        }

        throw err;
      } finally {
        setLoading(false);
      }
    },
    [defaultMethod, defaultHeaders, showToast, toast]
  );

  const api = {
    post: (url, body, options = {}) =>
      callApi(url, { method: "POST", body, ...options }),

    get: (url, params = {}, options = {}) =>
      callApi(url, { method: "GET", body: params, ...options }),

    put: (url, body, options = {}) =>
      callApi(url, { method: "PUT", body, ...options }),

    patch: (url, body, options = {}) =>
      callApi(url, { method: "PATCH", body, ...options }),

    delete: (url, params = {}, options = {}) =>
      callApi(url, { method: "DELETE", body: params, ...options }),
  };

  return {
    callApi,
    api,
    loading,
    error,
    data,
    setLoading,
    setError,
    setData,
  };
};

export default useApi;
